import { buildHairDresserPath, ROUTES, Paths } from '@shared/AppRoutes';
import { useIntl } from 'react-intl';
import { GuideStep } from './onboarding-guide-types';

export const useOnboardingGuide = () => {
  const intl = useIntl();
  const steps: GuideStep[] = [
    {
      key: 'appInstallGuide' as const,
      title: intl.formatMessage({
        id: 'dashboard.onboarding.appInstall.title',
        defaultMessage: 'Přidat aplikaci na plochu',
      }),
      description: intl.formatMessage({
        id: 'dashboard.onboarding.appInstall.description',
        defaultMessage: 'Otevírejte Kadeřník Plus rychle jako aplikaci přímo z plochy telefonu.',
      }),
      ctaLabel: intl.formatMessage({
        id: 'dashboard.onboarding.appInstall.cta',
        defaultMessage: 'Zobrazit postup',
      }),
      href: '#app-install-guide',
    },
    {
      key: 'workScheduleConfigured',
      title: intl.formatMessage({
        id: 'dashboard.onboarding.workSchedule.title',
        defaultMessage: 'Pracovní doba',
      }),
      description: intl.formatMessage({
        id: 'dashboard.onboarding.workSchedule.description',
        defaultMessage:
          'Nastavte si svoji pracovní dobu. Aplikace vám díky tomu sama navrhne vhodné termíny a kapacitu.',
      }),
      ctaLabel: intl.formatMessage({
        id: 'dashboard.onboarding.workSchedule.cta',
        defaultMessage: 'Nastavit pracovní dobu',
      }),

      href: buildHairDresserPath(ROUTES.profile.path),
    },
    {
      key: 'clientCreated',
      title: intl.formatMessage({
        id: 'dashboard.onboarding.clients.title',
        defaultMessage: 'Klienti',
      }),
      description: intl.formatMessage({
        id: 'dashboard.onboarding.clients.description',
        defaultMessage:
          'Přidejte klienty, abyste jejich správu měli na jednom místě. Jedná se o důležitou základní editaci.',
      }),
      ctaLabel: intl.formatMessage({
        id: 'dashboard.onboarding.clients.cta',
        defaultMessage: 'Otevřít klienty',
      }),

      href: buildHairDresserPath(ROUTES.clients.path),
    },
    {
      key: 'serviceCreated',
      title: intl.formatMessage({
        id: 'dashboard.onboarding.services.title',
        defaultMessage: 'Ceník',
      }),
      description: intl.formatMessage({
        id: 'dashboard.onboarding.services.description',
        defaultMessage:
          'Vytvořte si ceník služeb i s časovými sloty. Získáte možnost automatické navrhování volných termínů.',
      }),
      ctaLabel: intl.formatMessage({
        id: 'dashboard.onboarding.services.cta',
        defaultMessage: 'Otevřít ceník',
      }),

      href: buildHairDresserPath(ROUTES.services.path),
    },
    {
      key: 'stockItemCreated',
      title: intl.formatMessage({
        id: 'dashboard.onboarding.stock.title',
        defaultMessage: 'Sklad',
      }),
      description: intl.formatMessage({
        id: 'dashboard.onboarding.stock.description',
        defaultMessage:
          'Zapište do skladu všechny vaše materiály i zboží, které v současné době máte ve skříni v salonu.',
      }),
      ctaLabel: intl.formatMessage({
        id: 'dashboard.onboarding.stock.cta',
        defaultMessage: 'Otevřít sklad',
      }),

      href: Paths.stock('all'),
    },
    {
      key: 'qrPaymentsSetup',
      subscription: 'expert',
      title: intl.formatMessage({
        id: 'dashboard.onboarding.qrPayments.title',
        defaultMessage: 'QR platby',
      }),
      description: intl.formatMessage({
        id: 'dashboard.onboarding.qrPayments.description',
        defaultMessage:
          'Nastavte QR platby, aby vaši zákazníci mohli pohodlně platit a vy neztratili přehled.',
      }),
      ctaLabel: intl.formatMessage({
        id: 'dashboard.onboarding.qrPayments.cta',
        defaultMessage: 'Nastavit QR platby',
      }),
      href: buildHairDresserPath(ROUTES.profile.path),
    },
    {
      key: 'publicReservationSetup' as const,
      title: intl.formatMessage({
        id: 'dashboard.onboarding.publicReservation.title',
        defaultMessage: 'Rezervace nových zákazníků',
      }),
      subscription: 'expert',
      description: intl.formatMessage({
        id: 'dashboard.onboarding.publicReservation.description',
        defaultMessage:
          'Odkaz s vaším profilem, který vašim zákazníkům umožní se objednat. Jejich objednaní musíte vždy schválit. V přehledu objednávek mají panáčka.',
      }),
      ctaLabel: intl.formatMessage({
        id: 'dashboard.onboarding.publicReservation.cta',
        defaultMessage: 'Nastavit profil',
      }),
      href: buildHairDresserPath(ROUTES.profile.path),
    },
    {
      key: 'reservationEnabled',
      title: intl.formatMessage({
        id: 'dashboard.onboarding.reservation.title',
        defaultMessage: 'Automatické rezervace stálých zákazníků',
      }),
      subscription: 'expert',
      description: intl.formatMessage({
        id: 'dashboard.onboarding.reservation.description',
        defaultMessage:
          'Sami si vyberete, kteří zákazníci se budou moci objednat sami a systém je automaticky schválí. V přehledu objednávek mají panáčka.',
      }),
      ctaLabel: intl.formatMessage({
        id: 'dashboard.onboarding.reservation.cta',
        defaultMessage: 'Vybrat klienta',
      }),

      href: buildHairDresserPath(ROUTES.clients.path),
    },
    {
      key: 'visitCreated',
      title: intl.formatMessage({
        id: 'dashboard.onboarding.visitCreated.title',
        defaultMessage: 'Návštěva (objednávky)',
      }),
      description: intl.formatMessage({
        id: 'dashboard.onboarding.visitCreated.description',
        defaultMessage:
          'Přehled vašich objednávek, které si na začátku přepíšete z vašeho bloku uvidíte na základní obrazovce.',
      }),
      ctaLabel: intl.formatMessage({
        id: 'dashboard.onboarding.visitCreated.cta',
        defaultMessage: 'Otevřít návštěvy',
      }),

      href: buildHairDresserPath(ROUTES.visits.path),
    },
  ];

  return steps;
};
